import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Menu, X } from 'lucide-react';
import { BiCheckbox } from 'react-icons/bi';
import { Button } from 'react-bootstrap';
import axios from 'axios';

const FindOutMoreView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [serviceData, setServiceData] = useState(null);
  const [additionalContents, setAdditionalContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [error, setError] = useState(null);

  // API endpoints
  const API_BASE_URL = 'https://backend.dlapakistan.com/api/about';
  const SERVICE_BY_SLUG_URL = `${API_BASE_URL}/services/${slug}/`;
  const SERVICE_CONTENTS_URL = `${API_BASE_URL}/service-content/${slug}/`;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchServiceAndContents();
  }, [slug]);

  const fetchServiceAndContents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch service data
      const serviceResponse = await axios.get(SERVICE_BY_SLUG_URL);
      if (serviceResponse.data) {
        setServiceData(serviceResponse.data);
        
        // Fetch additional contents for this service
        await fetchAdditionalContents();
      } else {
        setError('Service not found');
      }
    } catch (err) {
      console.error('Error fetching service:', err);
      setError(err.response?.status === 404 ? 'Service not found' : 'Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalContents = async () => {
    try {
      setContentLoading(true);
      const response = await axios.get(SERVICE_CONTENTS_URL);
      
      // Check if response is array or single object
      if (response.data) {
        if (Array.isArray(response.data)) {
          setAdditionalContents(response.data);
        } else if (typeof response.data === 'object') {
          // If it's a single object, wrap it in array
          setAdditionalContents([response.data]);
        } else {
          setAdditionalContents([]);
        }
      } else {
        setAdditionalContents([]);
      }
    } catch (err) {
      console.error('Error fetching additional contents:', err);
      // If 404, set empty array
      if (err.response?.status === 404) {
        setAdditionalContents([]);
      } else {
        setError('Failed to load additional contents');
      }
    } finally {
      setContentLoading(false);
    }
  };

  // Sidebar links
  const sidebarLinks = [
    { text: "About Us", border: "#ccc", showIcon: false, path: "/about" },
    { text: "Meet the Board", border: "#ccc", showIcon: true, path: "/about/meet-the-board" },
    { text: "Work We Do", border: null, showIcon: true, path: "/about/work-we-do" },
    { text: "Education", border: null, showIcon: false, path: "/about/work-we-do/education" },
    { text: "Regulation", border: null, showIcon: false, path: "/about/work-we-do/regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/about/work-we-do/membership-support" },
    { text: "Our Policies", border: "#ccc", showIcon: true, path: "/about/our-policies" },
    { text: "Become a Member", border: "#ccc", showIcon: true, path: "/about/become-member" },
    { text: "DLA Fellowship", border: null, showIcon: true, path: "/about/dla-fellowship" },
    { text: "DLA Fellowship Awards", border: null, showIcon: false, path: "/about/dla-fellowship/awards" },
    { text: "DLA Special Recognition Award", border: "#ccc", showIcon: false, path: "/about/dla-fellowship/special-recognition" },
  ];

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isMobileView = windowWidth <= 1029;

  const getTopSpacing = () => {
    if (windowWidth < 640) return "pt-20";
    if (windowWidth < 768) return "pt-24";
    if (windowWidth < 1024) return "pt-28";
    if (windowWidth < 1175.56) return "pt-32";
    return "pt-40";
  };

  if (loading) {
    return (
      <div className={`w-full ${getTopSpacing()}`}>
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !serviceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "The service you're looking for doesn't exist."}</p>
          <Button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center mx-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${getTopSpacing()}`}>
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center mt-10 text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/about/work-we-do")}
          >
            Work We Do
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            {serviceData.title}
          </span>
        </div>

        {/* Main Content Layout with Sidebar */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          {/* Desktop Sidebar */}
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">About The DLA</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Main Content */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {serviceData.title}
              </h1>
              
              {/* Mobile Menu Button */}
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] px-3 py-2 rounded-md hover:bg-[#5d9a3a] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <X /> : <Menu />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>
           
            {/* Content Area */}
            <div className="mt-8 w-full pb-20">
              {contentLoading ? (
                <div className="text-center py-12 rounded-3xl">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading content...</p>
                </div>
              ) : additionalContents.length > 0 ? (
                <div className="grid grid-cols-1 gap-6">
                  {additionalContents.map((content) => (
                    <div
                      key={content.id}
                      className="bg-transparent rounded-lg overflow-hidden" // Changed from bg-white to bg-transparent
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-6">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{content.title}</h3>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-green-600" />
                          </div>
                        </div>

                        {/* Description */}
                        <div className="mt-6">
                          {content.description ? (
                            <div className="prose max-w-none">
                              <div 
                                className="text-gray-700"
                                dangerouslySetInnerHTML={{ __html: content.description }}
                              />
                            </div>
                          ) : (
                            <div className="text-center py-8 rounded-lg">
                              <p className="text-gray-400">No description available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-24 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Content Available</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    There is no content available for this service.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Sidebar */}
          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <X 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              onClick={toggleSidebar}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-6 border-t bg-gray-50 mt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              {/* Empty - Service ID removed */}
            </div>
            <div>
              <Button
                onClick={() => navigate("/about/work-we-do")}
                className="flex items-center gap-2 px-4 py-2 bg-[#70B444] text-white rounded-lg hover:bg-[#5d9a3a] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Work We Do
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style jsx>{`
        .prose {
          color: #374151;
          font-size: 15px;
          line-height: 1.7;
        }
        .prose p {
          margin-top: 0.75em;
          margin-bottom: 0.75em;
        }
        .prose ul, .prose ol {
          margin-top: 0.75em;
          margin-bottom: 0.75em;
          padding-left: 1.5em;
        }
        .prose li {
          margin-top: 0.25em;
          margin-bottom: 0.25em;
        }
        .prose h1, .prose h2, .prose h3, .prose h4 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default FindOutMoreView;